import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgGridAngular } from '@ag-grid-community/angular';
import { ColDef, GridApi, GridReadyEvent } from '@ag-grid-community/core';
import { SearchResult } from '../models/search-result.model';
import { ManufacturerItem } from '../models/manufacturer-item.model';
import { MaterialDesignation } from '../models/material-designation.model';
import { DimensionStandard } from '../models/dimension-standard.model';
import { MGMaterialGroupSpecificInstruction } from '../models/mg-material-group-specific-instruction';
import { AuthService } from '../services/auth.service';
import { ApiConfigService } from '../services/api-config.service';

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [CommonModule, FormsModule, AgGridAngular],
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit, OnDestroy {
  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  query: string = '';
  results: SearchResult | null = null;
  selectedTable: 'ManufacturerItems' | 'MaterialDesignations' | 'DimensionStandards' | 'MGMaterialGroupSpecificInstructions' | null = null;
  columnDefs: ColDef[] = [];
  public defaultColDef = {
    enableCellChangeFlash: true
  };
  gridOptions = {
    enableRangeSelection: true
  };
  rowData: (ManufacturerItem | MaterialDesignation | DimensionStandard | MGMaterialGroupSpecificInstruction)[] = [];
  private gridApi!: GridApi;
  isLoading: boolean = false;
  errorMessage: string = '';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private apiConfig: ApiConfigService
  ) {}

  ngOnInit(): void {
    window.addEventListener('resize', this.resizeColumns);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeColumns);
  }

  search(): void {
    this.errorMessage = '';
    if (this.query.trim().length < 3) {
      this.errorMessage = 'Search term must be at least 3 characters long.';
      return;
    }

    this.isLoading = true;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${this.authService.getToken()}`);
    
    this.http.get<SearchResult>(`${this.apiConfig.harmonizationSearchUrl}?q=${this.query}`, { headers })
      .subscribe(
        (data) => {
          this.results = data;
          this.isLoading = false;
          this.autoSelectTable();
        },
        (error) => {
          console.error('Error fetching search results', error);
          this.isLoading = false;
          this.errorMessage = 'An error occurred while fetching results. Please try again.';
        }
      );
  }

  autoSelectTable(): void {
    if (!this.results) return;

    const tables = [
      { name: 'ManufacturerItems', data: this.results.ManufacturerItemsJson.ManufacturerItems },
      { name: 'MaterialDesignations', data: this.results.MaterialDesignationsJson.MaterialDesignations },
      { name: 'DimensionStandards', data: this.results.DimensionStandardsJson.DimensionStandards },
      { name: 'MGMaterialGroupSpecificInstructions', data: this.results.MGMaterialGroupSpecificInstructionsJson?.MGMaterialGroupSpecificInstructions }
    ];

    const tablesWithData = tables.filter(table => table.data && table.data.length > 0);

    if (tablesWithData.length === 1) {
      this.selectTable(tablesWithData[0].name as 'ManufacturerItems' | 'MaterialDesignations' | 'DimensionStandards' | 'MGMaterialGroupSpecificInstructions');
    } else {
      this.selectedTable = null;
    }
  }

  selectTable(tableName: 'ManufacturerItems' | 'MaterialDesignations' | 'DimensionStandards' | 'MGMaterialGroupSpecificInstructions'): void {
    this.selectedTable = tableName;
    this.updateColumnDefs();
    this.updateRowData();
    setTimeout(() => this.resizeColumns(), 0);
  }

  updateColumnDefs(): void {
    if (this.results && this.selectedTable) {
      let firstItem: any;
      switch (this.selectedTable) {
        case 'ManufacturerItems':
          firstItem = this.results.ManufacturerItemsJson.ManufacturerItems[0];
          break;
        case 'MaterialDesignations':
          firstItem = this.results.MaterialDesignationsJson.MaterialDesignations[0];
          break;
        case 'DimensionStandards':
          firstItem = this.results.DimensionStandardsJson.DimensionStandards[0];
          break;
        case 'MGMaterialGroupSpecificInstructions':
          firstItem = this.results.MGMaterialGroupSpecificInstructionsJson?.MGMaterialGroupSpecificInstructions[0];
          break;
      }

      if (firstItem) {
        this.columnDefs = Object.keys(firstItem).map(key => ({
          field: key,
          headerName: this.formatHeaderName(key),
          sortable: true,
          filter: true,
          resizable: !this.checkIfColumnNeedsResizing(this.formatHeaderName(key)),
          maxWidth: this.checkIfColumnNeedsResizing(this.formatHeaderName(key)) ? 230 : undefined
        }));
      }
    } else {
      this.columnDefs = [];
    }
  }

  formatHeaderName(key: string): string {
    const headerName = key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    return headerName;
  }

  checkIfColumnNeedsResizing(key: string): boolean {
    const columnsToResize = ['matl grp description', 'comments', 'url'];
    return columnsToResize.some(column => key.toLowerCase().includes(column));
  }

  updateRowData(): void {
    if (this.results && this.selectedTable) {
      switch (this.selectedTable) {
        case 'ManufacturerItems':
          this.rowData = this.results.ManufacturerItemsJson.ManufacturerItems;
          break;
        case 'MaterialDesignations':
          this.rowData = this.results.MaterialDesignationsJson.MaterialDesignations;
          break;
        case 'DimensionStandards':
          this.rowData = this.results.DimensionStandardsJson.DimensionStandards;
          break;
        case 'MGMaterialGroupSpecificInstructions':
          this.rowData = this.results.MGMaterialGroupSpecificInstructionsJson?.MGMaterialGroupSpecificInstructions || [];
          break;
      }
    } else {
      this.rowData = [];
    }
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.resizeColumns();
  }

  resizeColumns = (): void => {
    if (this.gridApi) {
      this.gridApi.sizeColumnsToFit();
      this.gridApi.autoSizeAllColumns();
    }
  }

  onFilterTextBoxChanged(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.gridApi.setQuickFilter(inputElement.value);
  }

  onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.search();
    }
  }
}